import React, {
  FunctionComponent,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  ActionType,
  IRAInsulinCoachmarkType,
  Plan,
  SearchResultPlan,
} from "../../@types";
import { useHistory, useLocation } from "react-router";
import URI from "urijs";
import {
  CONSTANTS,
  logError,
  parsePlanLongIdIntoUrlPartForApi,
  isPdPlanType,
  useFormatMessage,
  makePlanLongId,
  useTranslate,
} from "../../helpers";
import { getPlan } from "../../api";
import { useFlags } from "launchdarkly-react-client-sdk";
import { AppStore } from "../../app/store";
import {
  AppPage,
  ActionsBar,
  EnrollmentDownAlert,
  FocusableHeader,
  LoadingMask,
  AlertMessage,
  HomeIcon,
  InsulinCoachmark,
} from "../../components";

import routes from "../../app/routes";
import Overview from "./Overview";
import BenefitsCosts from "./BenefitsCosts";
import ExtraBenefits from "./ExtraBenefits";
import { DrugCosts } from "./DrugCosts";
import { FormattedHTMLMessage, FormattedMessage } from "react-intl-phraseapp";
import {
  CountyName,
  PlanHeader,
  PlanSwitchOptions,
  YearOverYearWhatsNew,
  YearOverYearNextSteps,
} from "./components";
import { TotalDrugCosts } from "./TotalDrugCosts";
import { CoveredDrugs } from "./CoveredDrug";
import { alertMessageHierarchy } from "../../app/contexts/AlertMessaging/constants";
import { useAlertMessaging } from "../../app/contexts";
import { getMessageToShow } from "../../app/contexts/AlertMessaging/helpers";
import {
  AlertMessageConditions,
  AlertMessageHierarchyKeys,
} from "../../app/contexts/AlertMessaging/types";
import { ArrowIcon } from "@cmsgov/ds-medicare-gov";
import { Link } from "react-router-dom";
import { linkToContactMedicare } from "../../helpers/CONSTANTS";
import englishTranslations from "../../translations/en-US.json";
import { ApiError } from "../../helpers/errors";
import { fetchPlanPreferredPharmacyInfo } from "../../helpers/planHelpers";
import {
  AnalyticsActionType,
  Ga4ParentComponentType,
} from "../../app/contexts/Analytics/types";

export const planCompareAlertMessageTestId = "alert-message-plan-compare";
export const planCompareHeadingTranslationID = "plan_compare.you_are_comparing";
export const planCompareInsulinCoachmarkTestid =
  "plan-compare-insulin-coachmark";

export const PlanComparePage: FunctionComponent = () => {
  const location = useLocation();
  const history = useHistory();
  const t = useTranslate();
  const [plans, updatePlans] = useState<Plan[]>([]);
  const {
    state: {
      year,
      lis,
      futureLis,
      county,
      compareList,
      prescriptions,
      language,
      currentCoverage,
      csr,
    },
    dispatch,
  } = useContext(AppStore.AppContext);
  const hasPrescriptions = prescriptions.length > 0;
  const flags = useFlags();
  const { mctCurrentPlanYear = "" } = flags;
  const [selectedPlan, setSelectedPlan] = useState<Plan>();
  const plansRef = useRef(plans);
  const isYearOverYear = location.pathname === routes.yearOverYear;

  // Alert Messaging setup
  const {
    state: { messages: allAlertMessages },
  } = useAlertMessaging();
  const [networkStatusAlertMessages, setNetworkStatusAlertMessages] = useState<
    Record<string, JSX.Element | null>
  >({});
  const [generalAlertMessages, setGeneralAlertMessages] = useState<
    Record<string, JSX.Element | null>
  >({});
  const [renderMessageLocations, setRenderMessageLocations] = useState<
    Record<string, boolean>
  >({});
  // key = long plan id
  // value = boolean indicating if a plan covers preferred pharmacies or not
  const [
    plansPreferredPharmacyStatus,
    setPlansPreferredPharmacyStatus,
  ] = useState<Record<string, boolean>>({});
  const {
    displayMessageInDrugsCoveredRow,
    displayMessageInTotalDrugCostsRow,
    displayMessageAfterTotalDrugCosts,
  } = renderMessageLocations;

  const messageUpdater = (message: JSX.Element | null, planId: string) => (
    previousMessages: Record<string, JSX.Element | null>
  ) => {
    const updateNeeded =
      typeof previousMessages[planId] === "undefined" ||
      (previousMessages[planId] !== null && message === null) ||
      (previousMessages[planId] === null && message !== null);
    return updateNeeded
      ? {
          ...previousMessages,
          [planId]: message,
        }
      : previousMessages;
  };

  const updateNetworkStatusAlertMessage = (
    message: JSX.Element | null,
    planId: string
  ) => {
    setNetworkStatusAlertMessages(messageUpdater(message, planId));
  };

  useEffect(() => {
    let messageKey: AlertMessageConditions | -1 = -1;
    const planIds = plans.map(plan => makePlanLongId(plan));
    plans.forEach(plan => {
      const planId = makePlanLongId(plan);
      const generalAlertMessageForPlan = getMessageToShow(
        allAlertMessages,
        alertMessageHierarchy[AlertMessageHierarchyKeys.PLAN_COMPARISON_TABLE],
        t,
        {
          planId,
          planType: plan.plan_type,
          routerState: {
            nextRoute: {
              ...location,
            },
          },
        }
      );
      const message = generalAlertMessageForPlan ? (
        <AlertMessage
          {...{
            icon: generalAlertMessageForPlan.icon,
            body: generalAlertMessageForPlan.body,
            action: generalAlertMessageForPlan.action,
            condition: generalAlertMessageForPlan.key,
            title: generalAlertMessageForPlan.title,
            dataTestId: planCompareAlertMessageTestId,
            narrowWidth: true,
          }}
        />
      ) : null;
      setGeneralAlertMessages(messageUpdater(message, planId));
      if (messageKey === -1) {
        messageKey =
          typeof generalAlertMessageForPlan?.key !== "undefined"
            ? generalAlertMessageForPlan.key
            : -1;
      }
    });
    // Nullify messages for removed plans
    Object.keys(generalAlertMessages).forEach(alertMessageKey => {
      if (!planIds.some(planId => planId === alertMessageKey)) {
        setGeneralAlertMessages(messageUpdater(null, alertMessageKey));
      }
    });
    const generalMessageType = {
      noSavedPharmacies:
        messageKey === AlertMessageConditions.NO_SAVED_PHARMACIES,
      noSavedDrugs: messageKey === AlertMessageConditions.NO_SAVED_DRUGS,
      oneSavedPharmacy:
        messageKey === AlertMessageConditions.ONE_SAVED_PHARMACY,
    };
    setRenderMessageLocations({
      displayMessageAfterTotalDrugCosts:
        generalMessageType.oneSavedPharmacy && hasPrescriptions,
      displayMessageInDrugsCoveredRow:
        (generalMessageType.noSavedPharmacies && !hasPrescriptions) ||
        generalMessageType.noSavedDrugs,
      displayMessageInTotalDrugCostsRow:
        generalMessageType.noSavedPharmacies && hasPrescriptions,
    });
  }, [plans, allAlertMessages]);
  // End alert messaging setup

  useEffect(() => {
    (async () => {
      if (plans.length > 0) {
        const plansPreferredPharmaciesInfo = await fetchPlanPreferredPharmacyInfo(
          plans
        );
        if (plansPreferredPharmaciesInfo) {
          setPlansPreferredPharmacyStatus(plansPreferredPharmaciesInfo);
        }
      }
    })();
  }, [plans]);

  useEffect(() => {
    if (!compareList.length && !isYearOverYear) {
      history.push(routes.searchResults);
    }
  }, [compareList]);

  const validatePlanIds = (idList: string | (string | null)[] | null): void => {
    if (!idList) {
      throw new Error("Plans list variable was not provided in query");
    }

    // the URI api returns a string value when a single "plans" variable is provided
    if (typeof idList === "string") {
      throw new Error("Only one plan ID was provided");
    }

    if (idList.length < CONSTANTS.compareFooter.minPlansToCompare) {
      throw new Error(
        `The query string to the plan compare path requires a minimum of ${CONSTANTS.compareFooter.minPlansToCompare} IDs`
      );
    }

    // ensure that each longId can be split into fours
    if (!idList.every(longId => longId?.split("-").length === 4)) {
      throw new Error(
        `One or more plan ID values could not be split into four segments`
      );
    }
  };

  const getIdsFromQuery = (): string[] => {
    const hasQueryString = location && location.search;
    const locationURI = new URI(location.search);

    // true, if the query string contains atleast one plans variable
    const hasPlansList = locationURI.hasQuery("plans");

    let planIdList = null;
    if (hasQueryString && hasPlansList) {
      planIdList = locationURI.search(true).plans;
    } else {
      throw new Error("Plans list variable was not provided in query");
    }

    validatePlanIds(planIdList);
    return planIdList as string[];
  };

  useEffect(() => {
    let planIdList: string[] = [];

    try {
      planIdList = getIdsFromQuery();
    } catch (e) {
      const apiError = e as ApiError;
      console.error(
        "The following exception occurred while validating query parameters to the plan compare page: ",
        apiError.message
      );
    }

    const lisToSend =
      year !== mctCurrentPlanYear.toString() && futureLis ? futureLis : lis;

    Promise.all(
      (planIdList as string[]).map(id => {
        const apiURL = parsePlanLongIdIntoUrlPartForApi(id);
        return getPlan(apiURL, lisToSend ? { lis: lisToSend } : {});
      })
    )
      .then(plans =>
        plans.reduce((acc, plan) => {
          if (plan) acc.push(plan);
          return acc;
        }, [] as Plan[])
      )
      .then(planData => {
        updatePlans(planData);
        plansRef.current = planData;
      })
      .catch(error => {
        logError("Failed to retrieve plan (PlanComparisonPage)", error);
      });
  }, []);

  const determineSelectedPlan = () => {
    const isDesktop = window.matchMedia("(min-width: 1024px)").matches;

    if (isDesktop) {
      setSelectedPlan(undefined);
    } else {
      // Keep from resetting the plan if changing width below desktop breakpoint
      setSelectedPlan(prevPlanValue => {
        return prevPlanValue || plansRef.current[0];
      });
    }
  };

  useEffect(() => {
    const isDesktop = window.matchMedia("(min-width: 1024px)").matches;

    if (plans.length && !isDesktop) {
      setSelectedPlan(plans[0]);
    }
  }, [plans]);

  useEffect(() => {
    window.addEventListener("resize", determineSelectedPlan);

    return (): void =>
      window.removeEventListener("resize", determineSelectedPlan);
  }, []);

  const fm = useFormatMessage();
  const heading = fm(
    { id: planCompareHeadingTranslationID },
    { total: plans.length }
  );

  const onPlanRemove = useCallback(
    (planToRemove: Plan): void => {
      const removedPlanId = makePlanLongId(planToRemove);
      const removePlanFilter = (plan: Plan | SearchResultPlan): boolean =>
        plan.id !== planToRemove.id;

      updatePlans(plans.filter(removePlanFilter));

      dispatch({
        type: ActionType.UPDATE_COMPARE_LIST,
        payload: compareList.filter(removePlanFilter),
      });
      // Update alert messages so that this plan isn't included
      setNetworkStatusAlertMessages(messageUpdater(null, removedPlanId));
    },
    [plans, compareList]
  );

  return plans.length ? (
    <AppPage
      heading={heading}
      headingClass="ds-u-display--none"
      bannerClassName="ds-u-padding--0"
      headerExtension={
        isYearOverYear ? (
          <ul className="YearOverYear__navigation">
            {!csr && (
              <li>
                <HomeIcon className="ds-u-margin-right--1 ds-u-color--primary" />{" "}
                <a
                  className="ds-u-color--primary"
                  href={CONSTANTS.linkToAccountHome(language)}
                >
                  {t("summary_page.links.account_home")}
                </a>
              </li>
            )}
            <li>
              <ArrowIcon
                direction="left"
                className="ds-u-margin-right--1 ds-u-color--primary"
              />{" "}
              <Link
                to={csr ? routes.csrLanding : routes.summary.landingPage}
                className="ds-u-color--primary"
              >
                {t("back")}
              </Link>
            </li>
          </ul>
        ) : (
          <></>
        )
      }
    >
      <FocusableHeader className="e2e-pc-comparing" text={heading} srOnly />

      {!isYearOverYear && (
        <ActionsBar
          extraContent={
            county && (
              <CountyName
                className="PlanComparePage__countyName--tablet"
                county={county}
              />
            )
          }
          extraContentClassName="ds-u-lg-display--none ds-u-display--none ds-u-md-display--block ds-u-text-align--center"
          to={routes.searchResults}
          withPrint
          printEventAction="Find Plans - Compare Plans"
        >
          {<FormattedHTMLMessage id="plan_compare.go_back_to_plan_results" />}
        </ActionsBar>
      )}

      {!isYearOverYear && county && (
        <CountyName
          className="PlanComparePage__countyName--mobileDesktop ds-u-md-display--none ds-u-lg-display--block"
          county={county}
        />
      )}
      <EnrollmentDownAlert className="ds-u-margin-bottom--2 ds-u-lg-margin-bottom--4 ds-u-margin-top--2 ds-u-lg-margin-top--0" />

      {isYearOverYear && (
        <YearOverYearWhatsNew
          currentCoverage={currentCoverage || plans[0]}
          language={language}
          currentPlanYear={mctCurrentPlanYear}
          className="ds-u-margin-bottom--7"
        />
      )}

      <PlanSwitchOptions
        plans={plans}
        selectedPlan={selectedPlan}
        setSelectedPlan={setSelectedPlan}
      />
      {/* Display the plan header only in mobile view */}
      {selectedPlan && (
        <div className="ds-u-lg-display--none mct-hide-print">
          <PlanHeader
            isYearOverYear={isYearOverYear}
            plan={selectedPlan}
            noLeftBorder
          />
        </div>
      )}
      <table className="PlanComparePage__table">
        {/* Display the table header only in desktop view */}
        {!selectedPlan && (
          <thead>
            <tr>
              <td className="PlanComparePage__floatingHeader">
                <div className="PlanComparePage__planHeader ds-u-border-left--0"></div>
              </td>
              {plans.map(plan => (
                <td className="PlanComparePage__floatingHeader" key={plan.id}>
                  <PlanHeader
                    isYearOverYear={isYearOverYear}
                    plan={plan}
                    onPlanRemove={onPlanRemove}
                  />
                </td>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          <Overview
            plans={plans}
            selectedPlan={selectedPlan}
            isYearOverYear={isYearOverYear}
          />
          {!isPdPlanType(plans[0].plan_type) && (
            <BenefitsCosts plans={plans} selectedPlan={selectedPlan} />
          )}
          {!isPdPlanType(plans[0].plan_type) && (
            <ExtraBenefits plans={plans} selectedPlan={selectedPlan} />
          )}
          {hasPrescriptions && (
            <DrugCosts
              networkStatusAlertMessages={networkStatusAlertMessages}
              generalAlertMessages={generalAlertMessages}
              plansPreferredPharmacyStatus={plansPreferredPharmacyStatus}
              coveredDrugs={plans.map((plan, i) => {
                const planId = makePlanLongId(plan);
                const planGeneralAlertMessage =
                  displayMessageInDrugsCoveredRow &&
                  generalAlertMessages[planId];
                return (
                  <CoveredDrugs
                    generalAlertMessage={
                      planGeneralAlertMessage
                        ? planGeneralAlertMessage
                        : undefined
                    }
                    key={i}
                    plan={plan}
                    prescriptions={prescriptions}
                    selectedPlan={selectedPlan}
                    hasPrescriptions={hasPrescriptions}
                  />
                );
              })}
              displayMessageAfterTotalDrugCosts={
                displayMessageAfterTotalDrugCosts
              }
              displayMessageInDrugsCoveredRow={displayMessageInDrugsCoveredRow}
              displayMessageInTotalDrugCostsRow={
                displayMessageInTotalDrugCostsRow
              }
              hasPrescriptions={hasPrescriptions}
              isYearOverYear={isYearOverYear}
              plans={plans}
              selectedPlan={selectedPlan}
              totalDrugCosts={plans.map((plan, i) => {
                const planId = makePlanLongId(plan);
                const planGeneralAlertMessage =
                  displayMessageInTotalDrugCostsRow &&
                  generalAlertMessages[planId];
                return (
                  <TotalDrugCosts
                    generalAlertMessage={
                      planGeneralAlertMessage
                        ? planGeneralAlertMessage
                        : undefined
                    }
                    key={i}
                    plan={plan}
                    planId={planId}
                    selectedPlan={selectedPlan}
                    updateNetworkStatusAlertMessage={
                      updateNetworkStatusAlertMessage
                    }
                    isYearOverYear={isYearOverYear}
                  />
                );
              })}
            />
          )}
        </tbody>
      </table>

      <InsulinCoachmark
        className="PlanComparePage__coachmark"
        dataTestId={planCompareInsulinCoachmarkTestid}
        coachmarkType={IRAInsulinCoachmarkType.PLAN_COMPARE}
        body={
          <>
            <FormattedMessage
              id="coachmark.insulin.plan_compare.body.paragraph.one"
              values={{
                emphasized_text: (
                  <strong>
                    {t(
                      "coachmark.insulin.plan_compare.body.paragraph.one.emphasized_text"
                    )}
                  </strong>
                ),
              }}
              tagName="p"
            />
            <FormattedMessage
              id="coachmark.insulin.plan_compare.body.paragraph.two"
              values={{
                emphasized_text: (
                  <strong>
                    {t(
                      "coachmark.insulin.plan_compare.body.paragraph.two.emphasized_text"
                    )}
                  </strong>
                ),
                link: (
                  <a
                    href={linkToContactMedicare(language)}
                    rel="noopener noreferrer"
                    target="_blank"
                    onClick={() => {
                      const linkText =
                        englishTranslations[
                          "coachmark.insulin.plan_compare.body.paragraph.two.link_text"
                        ];
                      dispatch({
                        type:
                          AnalyticsActionType.SEND_INTERNAL_LINK_CLICKED_EVENT,
                        settings: {
                          linkText,
                          linkUrl: linkToContactMedicare(language),
                          custom: {
                            event_action: "ira internal link clicked",
                            link_text: `coach mark: ${linkText}`,
                            parent_component_heading: "ira coach mark",
                            parent_component_type: Ga4ParentComponentType.ALERT,
                          },
                        },
                      });
                    }}
                  >
                    {t(
                      "coachmark.insulin.plan_compare.body.paragraph.two.link_text"
                    )}
                  </a>
                ),
              }}
              tagName="p"
            />
          </>
        }
      />

      {isYearOverYear && <YearOverYearNextSteps />}
    </AppPage>
  ) : (
    <LoadingMask loading={plans.length === 0} />
  );
};
